// EditFlow.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";
import { apiURL } from "../config/config";
import BackButton from "../components/BackButton"; // ✅ ปุ่มย้อนกลับ

const EditFlow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flowName, setFlowName] = useState("");
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        const response = await axios.get(`${apiURL}approve-flows/${id}`);
        const flowData = response.data.flow;
        setFlowName(flowData.name);
        setLevels(flowData.approvalHierarchy || []);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "ไม่สามารถโหลดข้อมูล Flow ได้",
          text: "โปรดลองอีกครั้งภายหลัง",
          confirmButtonColor: "#ef4444",
          background: "#1f2937",
          color: "#fff",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFlow();
  }, [id]);

  const addLevel = () => {
    const existingLevels = levels.map((l) => l.level).sort((a, b) => a - b);
    let newLevel = 2;

    for (let i = 2; i <= Math.max(...existingLevels, 2); i++) {
      if (!existingLevels.includes(i)) {
        newLevel = i;
        break;
      }
    }

    if (existingLevels.includes(newLevel)) {
      newLevel = Math.max(...existingLevels, 1) + 1;
    }

    setLevels(
      [...levels, { level: newLevel, approver: "" }].sort(
        (a, b) => a.level - b.level
      )
    );
  };

  const removeLevel = (index) => {
    setLevels(levels.filter((_, i) => i !== index));
  };

  const handleLevelChange = (index, value) => {
    const updatedLevels = [...levels];
    updatedLevels[index].approver = value;
    setLevels(updatedLevels);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (
      !flowName ||
      levels.some((level) => !/\S+@\S+\.\S+/.test(level.approver))
    ) {
      Swal.fire({
        icon: "error",
        title: "ข้อมูลไม่ถูกต้อง",
        text: "กรุณากรอกชื่อ Flow และอีเมลให้ถูกต้องทุกช่อง",
        confirmButtonColor: "#ef4444",
        background: "#1f2937",
        color: "#fff",
      });
      return;
    }

    setSaving(true);

    const payload = {
      name: flowName,
      approvalHierarchy: levels.map((level) => ({
        level: level.level,
        approver: level.approver,
      })),
    };

    try {
      await axios.put(`${apiURL}approve-flows/update/${id}`, payload);

      await Swal.fire({
        icon: "success",
        title: "อัปเดต Flow สำเร็จ",
        showConfirmButton: false,
        timer: 1500,
        background: "#1f2937",
        color: "#fff",
      });

      navigate("/manage-flows");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถอัปเดต Flow ได้ โปรดลองอีกครั้ง",
        confirmButtonColor: "#ef4444",
        background: "#1f2937",
        color: "#fff",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "ต้องการลบ Flow นี้หรือไม่?",
      text: "การลบจะไม่สามารถกู้คืนได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "ลบเลย",
      cancelButtonText: "ยกเลิก",
      background: "#1f2937",
      color: "#fff",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${apiURL}approve-flows/delete/${id}`);

        await Swal.fire({
          icon: "success",
          title: "ลบ Flow สำเร็จ",
          showConfirmButton: false,
          timer: 1500,
          background: "#1f2937",
          color: "#fff",
        });

        navigate("/manage-flows");
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "ไม่สามารถลบได้",
          text: "โปรดลองอีกครั้งภายหลัง",
          confirmButtonColor: "#ef4444",
          background: "#1f2937",
          color: "#fff",
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-50">
        <ClipLoader color="#6366F1" loading={loading} size={80} />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 md:px-6 py-6 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Header Section */}
      <div className="max-w-3xl mx-auto flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-gray-200">
          Edit Approve Flow
        </h1>
        <BackButton label="ย้อนกลับ" />
      </div>

      {/* Form Section */}
      <form
        onSubmit={handleUpdate}
        className="space-y-5 bg-white dark:bg-gray-800 shadow-md rounded-xl p-6 border border-gray-200 dark:border-gray-700 max-w-3xl mx-auto"
      >
        {/* Flow Name */}
        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
            Flow Name
          </label>
          <input
            type="text"
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:bg-gray-700 dark:text-white placeholder-gray-400"
            placeholder="Enter flow name"
          />
        </div>

        {/* Approval Levels */}
        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
            Approval Levels
          </label>
          <div className="space-y-3">
            {levels.map((level, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0 bg-gray-100 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <span className="font-medium text-gray-700 dark:text-gray-200 w-full sm:w-auto">
                  Level {level.level}
                </span>
                <input
                  type="email"
                  value={level.approver}
                  onChange={(e) => handleLevelChange(index, e.target.value)}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:bg-gray-800 dark:text-white placeholder-gray-400"
                  placeholder="Approver email"
                />
                <button
                  type="button"
                  onClick={() => removeLevel(index)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    levels.length === 1
                      ? "bg-gray-400 cursor-not-allowed text-white"
                      : "bg-red-500 hover:bg-red-600 text-white"
                  }`}
                  disabled={levels.length === 1}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Add Level */}
        <button
          type="button"
          onClick={addLevel}
          className="w-full bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-all font-medium"
        >
          Add Level
        </button>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-3">
          <button
            type="button"
            onClick={handleDelete}
            className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-lg font-medium transition-all"
          >
            Delete
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg flex items-center justify-center font-medium transition-all"
          >
            {saving ? (
              <ClipLoader size={18} color="#ffffff" />
            ) : (
              "Update Flow"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditFlow;
